import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceInput = ({ onTranscription, label = "Say it loud" }) => {
  const [state, setState] = useState('idle'); // idle | recording | transcribing
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        setState('transcribing');
        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');
          const response = await fetch(`${API_URL}/api/voice/transcribe`, {
            method: 'POST',
            body: formData,
          });
          if (!response.ok) throw new Error('Transcription failed');
          const data = await response.json();
          if (data.success && data.text) {
            onTranscription(data.text);
            toast.success('Voice transcribed!');
          } else {
            throw new Error('No transcription received');
          }
        } catch {
          toast.error('Could not transcribe. Please try again.');
        }
        setState('idle');
      };

      mediaRecorderRef.current.start(1000);
      setState('recording');
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } catch {
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && state === 'recording') {
      mediaRecorderRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  if (state === 'transcribing') {
    return (
      <span className="inline-flex items-center gap-1.5 text-purple-600 text-sm font-accent">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Transcribing...
      </span>
    );
  }

  if (state === 'recording') {
    return (
      <Button
        type="button"
        data-testid="voice-stop-inline-btn"
        onClick={stopRecording}
        variant="ghost"
        size="sm"
        className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full gap-1.5"
      >
        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
        <span className="text-xs font-mono">{formatTime(recordingTime)}</span>
        <Square className="w-3 h-3" />
      </Button>
    );
  }

  return (
    <Button
      type="button"
      data-testid="voice-say-it-loud-btn"
      onClick={startRecording}
      variant="ghost"
      size="sm"
      className="h-8 px-3 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-full gap-1.5"
    >
      <Mic className="w-3.5 h-3.5" />
      <span className="text-xs font-accent">{label}</span>
    </Button>
  );
};

export default VoiceInput;
